import {useEffect, useState} from 'react'
import {useMutation} from '@apollo/client'
import {Button, Col, Form, InputGroup, Row} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {
  CONFIRM_TWO_FACTOR_SETUP,
  DISABLE_TWO_FACTOR,
  START_TWO_FACTOR_SETUP,
} from '../../gql/mutations/authMutations'
import {
  RESET_USER_TWO_FACTOR,
  SET_USER_TWO_FACTOR_EMAIL_FALLBACK,
  SET_USER_TWO_FACTOR_REQUIRED,
} from '../../gql/mutations/userMutations'
import {
  IResConfirmTwoFactorSetup,
  IResDisableTwoFactor,
  IResStartTwoFactorSetup,
} from '../../types/auth'

type ResetTwoFactorResponse = {
  resetUserTwoFactorMutation: {
    success: boolean
    twoFactorEnabled?: boolean | null
    twoFactorEmailFallbackEnabled?: boolean | null
    errors?: {field?: string | null; message: string}[] | null
  }
}

type SetUserTwoFactorEmailFallbackResponse = {
  setUserTwoFactorEmailFallbackMutation: {
    success: boolean
    twoFactorEmailFallbackEnabled?: boolean | null
    errors?: {field?: string | null; message: string}[] | null
  }
}

type SetUserTwoFactorRequiredResponse = {
  setUserTwoFactorRequiredMutation: {
    success: boolean
    twoFactorRequired?: boolean | null
    errors?: {field?: string | null; message: string}[] | null
  }
}

interface TwoFactorSettingsPanelProps {
  twoFactorEnabled: boolean
  twoFactorRequired?: boolean
  twoFactorEmailFallbackEnabled?: boolean
  canManageOwnTwoFactor?: boolean
  canResetTwoFactor?: boolean
  showAdminRequiredControl?: boolean
  targetUserId?: string | number | null
  onUpdated?: () => void | Promise<unknown>
}

const getErrorMessage = (error: any, fallback: string) => {
  if (error?.graphQLErrors?.[0]?.message) return error.graphQLErrors[0].message
  if (typeof error?.message === 'string' && error.message.trim()) return error.message
  return fallback
}

export const TwoFactorSettingsPanel = ({
  twoFactorEnabled,
  twoFactorRequired = false,
  twoFactorEmailFallbackEnabled = false,
  canManageOwnTwoFactor = false,
  canResetTwoFactor = false,
  showAdminRequiredControl = true,
  targetUserId,
  onUpdated,
}: TwoFactorSettingsPanelProps) => {
  const [twoFactorSetupKey, setTwoFactorSetupKey] = useState<string | null>(null)
  const [twoFactorSetupUrl, setTwoFactorSetupUrl] = useState<string | null>(null)
  const [twoFactorSetupQrSvg, setTwoFactorSetupQrSvg] = useState<string | null>(null)
  const [twoFactorSetupCode, setTwoFactorSetupCode] = useState('')
  const [disableTwoFactorPassword, setDisableTwoFactorPassword] = useState('')
  const [disableTwoFactorCode, setDisableTwoFactorCode] = useState('')
  const [adminTwoFactorRequired, setAdminTwoFactorRequired] = useState(Boolean(twoFactorRequired))
  const [adminEmailFallbackEnabled, setAdminEmailFallbackEnabled] = useState(
    Boolean(twoFactorEmailFallbackEnabled)
  )

  const [gqlStartTwoFactorSetup, {loading: loadingStartTwoFactorSetup}] =
    useMutation<IResStartTwoFactorSetup>(START_TWO_FACTOR_SETUP)
  const [gqlConfirmTwoFactorSetup, {loading: loadingConfirmTwoFactorSetup}] =
    useMutation<IResConfirmTwoFactorSetup>(CONFIRM_TWO_FACTOR_SETUP)
  const [gqlDisableTwoFactor, {loading: loadingDisableTwoFactor}] =
    useMutation<IResDisableTwoFactor>(DISABLE_TWO_FACTOR)
  const [gqlResetTwoFactor, {loading: loadingResetTwoFactor}] =
    useMutation<ResetTwoFactorResponse>(RESET_USER_TWO_FACTOR)
  const [gqlSetUserTwoFactorRequired, {loading: loadingSetUserTwoFactorRequired}] =
    useMutation<SetUserTwoFactorRequiredResponse>(SET_USER_TWO_FACTOR_REQUIRED)
  const [gqlSetUserTwoFactorEmailFallback, {loading: loadingSetUserTwoFactorEmailFallback}] =
    useMutation<SetUserTwoFactorEmailFallbackResponse>(SET_USER_TWO_FACTOR_EMAIL_FALLBACK)

  useEffect(() => {
    setAdminTwoFactorRequired(Boolean(twoFactorRequired))
  }, [twoFactorRequired])

  useEffect(() => {
    setAdminEmailFallbackEnabled(Boolean(twoFactorEmailFallbackEnabled))
  }, [twoFactorEmailFallbackEnabled])

  const runUpdated = async () => {
    if (onUpdated) {
      await Promise.resolve(onUpdated())
    }
  }

  const handleCopy = async (value: string, label: string) => {
    if (!value) return

    if (!navigator?.clipboard?.writeText) {
      toast.info(`Copy the ${label} manually.`)
      return
    }

    try {
      await navigator.clipboard.writeText(value)
      toast.success(`${label} copied.`)
    } catch (_) {
      toast.error(`Unable to copy the ${label}.`)
    }
  }

  const handleStartTwoFactorSetup = async () => {
    try {
      const response = await gqlStartTwoFactorSetup()
      const result = response.data?.startTwoFactorSetup

      if (!result?.success) {
        toast.error(result?.message || 'Unable to start two-factor setup.')
        return
      }

      setTwoFactorSetupKey(result.manualEntryKey || null)
      setTwoFactorSetupUrl(result.otpauthUrl || null)
      setTwoFactorSetupQrSvg(result.qrCodeSvg || null)
      setTwoFactorSetupCode('')
      toast.success(result.message || 'Two-factor setup started.')
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Unable to start two-factor setup.'))
    }
  }

  const handleConfirmTwoFactorSetup = async () => {
    if (!twoFactorSetupCode.trim()) {
      toast.error('Verification code is required.')
      return
    }

    try {
      const response = await gqlConfirmTwoFactorSetup({
        variables: {
          code: twoFactorSetupCode.trim(),
        },
      })
      const result = response.data?.confirmTwoFactorSetup

      if (!result?.success) {
        toast.error(result?.message || 'Unable to confirm two-factor setup.')
        return
      }

      toast.success(result.message || 'Two-factor authentication enabled.')
      setTwoFactorSetupKey(null)
      setTwoFactorSetupUrl(null)
      setTwoFactorSetupQrSvg(null)
      setTwoFactorSetupCode('')
      await runUpdated()
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Unable to confirm two-factor setup.'))
    }
  }

  const handleDisableTwoFactor = async () => {
    if (!disableTwoFactorPassword.trim()) {
      toast.error('Current password is required.')
      return
    }

    if (!disableTwoFactorCode.trim()) {
      toast.error('Authenticator code is required.')
      return
    }

    try {
      const response = await gqlDisableTwoFactor({
        variables: {
          password: disableTwoFactorPassword.trim(),
          code: disableTwoFactorCode.trim(),
        },
      })
      const result = response.data?.disableTwoFactor

      if (!result?.success) {
        toast.error(result?.message || 'Unable to disable two-factor authentication.')
        return
      }

      toast.success(result.message || 'Two-factor authentication disabled.')
      setDisableTwoFactorPassword('')
      setDisableTwoFactorCode('')
      setTwoFactorSetupKey(null)
      setTwoFactorSetupUrl(null)
      setTwoFactorSetupQrSvg(null)
      setTwoFactorSetupCode('')
      await runUpdated()
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Unable to disable two-factor authentication.'))
    }
  }

  const handleResetTwoFactor = async () => {
    if (!targetUserId) return

    try {
      const response = await gqlResetTwoFactor({
        variables: {
          userId: targetUserId,
        },
      })

      const result = response.data?.resetUserTwoFactorMutation
      if (!result?.success) {
        const message = result?.errors?.[0]?.message || 'Unable to reset two-factor authentication.'
        toast.error(message)
        return
      }

      toast.success('Two-factor authentication was reset for this user.')
      await runUpdated()
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Unable to reset two-factor authentication.'))
    }
  }

  const handleToggleEmailFallback = async (enabled: boolean) => {
    if (!targetUserId) return

    const previousValue = adminEmailFallbackEnabled
    setAdminEmailFallbackEnabled(enabled)

    try {
      const response = await gqlSetUserTwoFactorEmailFallback({
        variables: {
          userId: targetUserId,
          enabled,
        },
      })

      const result = response.data?.setUserTwoFactorEmailFallbackMutation
      if (!result?.success) {
        setAdminEmailFallbackEnabled(previousValue)
        const message =
          result?.errors?.[0]?.message || 'Unable to update email fallback for this user.'
        toast.error(message)
        return
      }

      setAdminEmailFallbackEnabled(Boolean(result.twoFactorEmailFallbackEnabled))
      toast.success(
        enabled
          ? 'Email fallback enabled for this user.'
          : 'Email fallback disabled for this user.'
      )
      await runUpdated()
    } catch (error: any) {
      setAdminEmailFallbackEnabled(previousValue)
      toast.error(getErrorMessage(error, 'Unable to update email fallback for this user.'))
    }
  }

  const handleToggleTwoFactorRequired = async (enabled: boolean) => {
    if (!targetUserId) return

    const previousValue = adminTwoFactorRequired
    setAdminTwoFactorRequired(enabled)

    try {
      const response = await gqlSetUserTwoFactorRequired({
        variables: {
          userId: targetUserId,
          enabled,
        },
      })

      const result = response.data?.setUserTwoFactorRequiredMutation
      if (!result?.success) {
        setAdminTwoFactorRequired(previousValue)
        const message =
          result?.errors?.[0]?.message || 'Unable to update the 2FA requirement for this user.'
        toast.error(message)
        return
      }

      setAdminTwoFactorRequired(Boolean(result.twoFactorRequired))
      toast.success(enabled ? '2FA is now required for this user.' : '2FA requirement removed.')
      await runUpdated()
    } catch (error: any) {
      setAdminTwoFactorRequired(previousValue)
      toast.error(getErrorMessage(error, 'Unable to update the 2FA requirement for this user.'))
    }
  }

  const hasAdminPolicyAccess = !canManageOwnTwoFactor && canResetTwoFactor && !!targetUserId
  const showResetAction = hasAdminPolicyAccess && twoFactorEnabled
  const showAdminRequiredToggle = hasAdminPolicyAccess && showAdminRequiredControl
  const showAdminEmailFallbackToggle = hasAdminPolicyAccess
  const effectiveTwoFactorRequired = showAdminRequiredToggle
    ? adminTwoFactorRequired
    : Boolean(twoFactorRequired)
  const effectiveEmailFallbackEnabled = showAdminEmailFallbackToggle
    ? adminEmailFallbackEnabled
    : Boolean(twoFactorEmailFallbackEnabled)
  const canDisableTwoFactor = canManageOwnTwoFactor && twoFactorEnabled && !effectiveTwoFactorRequired

  return (
    <div>
      <div className='d-flex justify-content-between align-items-start flex-wrap gap-3 mb-6'>
        <div>
          <div className='d-flex align-items-center gap-3 mb-2 flex-wrap'>
            <span className={`badge badge-light-${twoFactorEnabled ? 'success' : 'secondary'}`}>
              {twoFactorEnabled ? 'Enabled' : 'Disabled'}
            </span>
            {effectiveTwoFactorRequired && (
              <span className='badge badge-light-warning'>Required</span>
            )}
            {twoFactorEnabled && effectiveEmailFallbackEnabled && (
              <span className='text-muted fs-7'>Email fallback is enabled for login recovery.</span>
            )}
          </div>
          <div className='text-gray-600 fs-6'>
            Primary method: authenticator app. Secondary method: email code during login if the
            app is unavailable.
          </div>
        </div>

        {canManageOwnTwoFactor && !twoFactorEnabled && !twoFactorSetupKey && (
          <Button
            type='button'
            className='btn btn-primary'
            onClick={handleStartTwoFactorSetup}
            disabled={loadingStartTwoFactorSetup}
          >
            {!loadingStartTwoFactorSetup && 'Enable 2FA'}
            {loadingStartTwoFactorSetup && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </Button>
        )}

        {showResetAction && (
          <Button
            type='button'
            className='btn btn-light-danger'
            onClick={handleResetTwoFactor}
            disabled={loadingResetTwoFactor}
          >
            {!loadingResetTwoFactor && 'Reset 2FA'}
            {loadingResetTwoFactor && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </Button>
        )}
      </div>

      {showAdminRequiredToggle && (
        <div className='border rounded p-5 bg-light mb-6'>
          <div className='fw-bold mb-2'>2FA requirement</div>
          <div className='text-muted mb-4'>
            Require this user to configure and keep two-factor authentication enabled.
          </div>

          <div className='form-check form-switch form-check-custom form-check-solid'>
            <input
              className='form-check-input'
              type='checkbox'
              id={`two_factor_required_${String(targetUserId)}`}
              checked={effectiveTwoFactorRequired}
              disabled={loadingSetUserTwoFactorRequired}
              onChange={(e) => handleToggleTwoFactorRequired(e.target.checked)}
            />
            <label
              className='form-check-label fw-semibold'
              htmlFor={`two_factor_required_${String(targetUserId)}`}
            >
              Require 2FA setup
            </label>
          </div>

          <div className='text-muted fs-7 mt-3'>
            {twoFactorEnabled
              ? 'If this remains enabled, the user will not be able to disable 2FA.'
              : 'If this is enabled, the user will be redirected to profile and must configure 2FA at next login.'}
          </div>
        </div>
      )}

      {showAdminEmailFallbackToggle && (
        <div className='border rounded p-5 bg-light mb-6'>
          <div className='fw-bold mb-2'>Email fallback for login recovery</div>
          <div className='text-muted mb-4'>
            Allow this user to receive a 6-digit code by email during login if they cannot access
            their authenticator app.
          </div>

          <div className='form-check form-switch form-check-custom form-check-solid'>
            <input
              className='form-check-input'
              type='checkbox'
              id={`two_factor_email_fallback_${String(targetUserId)}`}
              checked={effectiveEmailFallbackEnabled}
              disabled={loadingSetUserTwoFactorEmailFallback}
              onChange={(e) => handleToggleEmailFallback(e.target.checked)}
            />
            <label
              className='form-check-label fw-semibold'
              htmlFor={`two_factor_email_fallback_${String(targetUserId)}`}
            >
              Allow email fallback
            </label>
          </div>

          <div className='text-muted fs-7 mt-3'>
            {twoFactorEnabled
              ? 'This takes effect immediately on the next 2FA login challenge.'
              : 'This setting will be used when the user enables 2FA.'}
          </div>
        </div>
      )}

      {canManageOwnTwoFactor && effectiveTwoFactorRequired && !twoFactorEnabled && (
        <div className='alert alert-warning mb-6'>
          Two-factor authentication is required for your account. Configure it before leaving this
          page.
        </div>
      )}

      {canManageOwnTwoFactor && !twoFactorEnabled && twoFactorSetupKey && (
        <div className='border rounded p-5 bg-light'>
          <div className='fw-bold mb-2'>Add this account to your authenticator app</div>
          <div className='text-muted mb-4'>
            Scan the QR with Google Authenticator or Microsoft Authenticator. If you cannot scan it,
            use the setup code below and then enter the 6-digit code generated by the app.
          </div>

          <Row className='g-4'>
            <Col md={5} sm={12}>
              <div className='d-flex flex-column align-items-center justify-content-center h-100'>
                {twoFactorSetupQrSvg ? (
                  <div
                    className='bg-white border rounded p-3'
                    style={{maxWidth: '220px'}}
                    dangerouslySetInnerHTML={{__html: twoFactorSetupQrSvg}}
                  />
                ) : (
                  <div className='text-muted fs-7'>QR not available. Use the setup code below.</div>
                )}
                <div className='text-muted fs-8 mt-3 text-center'>
                  Scan from your phone to add this account quickly.
                </div>
              </div>
            </Col>

            <Col md={7} sm={12}>
              <Form.Group>
                <Form.Label>Authenticator setup code</Form.Label>
                <InputGroup>
                  <Form.Control readOnly value={twoFactorSetupKey} />
                  <Button
                    type='button'
                    variant='light-primary'
                    onClick={() => handleCopy(twoFactorSetupKey, 'setup code')}
                  >
                    Copy
                  </Button>
                </InputGroup>
                <Form.Text className='text-muted'>
                  Use this code if the app asks for a key instead of scanning a QR.
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={12} sm={12}>
              <Form.Group>
                <Form.Label>6-digit verification code</Form.Label>
                <Form.Control
                  value={twoFactorSetupCode}
                  onChange={(e) =>
                    setTwoFactorSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder='000000'
                  inputMode='numeric'
                />
              </Form.Group>
            </Col>
          </Row>

          {twoFactorSetupUrl && (
            <details className='mt-4'>
              <summary className='text-primary fw-semibold'>Advanced setup link</summary>
              <div className='mt-3'>
                <InputGroup>
                  <Form.Control readOnly value={twoFactorSetupUrl} />
                  <Button
                    type='button'
                    variant='light-primary'
                    onClick={() => handleCopy(twoFactorSetupUrl, 'setup link')}
                  >
                    Copy
                  </Button>
                </InputGroup>
              </div>
            </details>
          )}

          <div className='d-flex gap-3 flex-wrap mt-5'>
            <Button
              type='button'
              className='btn btn-success'
              onClick={handleConfirmTwoFactorSetup}
              disabled={loadingConfirmTwoFactorSetup}
            >
              {!loadingConfirmTwoFactorSetup && 'Confirm and enable'}
              {loadingConfirmTwoFactorSetup && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </Button>

            <Button
              type='button'
              variant='light'
              onClick={() => {
                setTwoFactorSetupKey(null)
                setTwoFactorSetupUrl(null)
                setTwoFactorSetupQrSvg(null)
                setTwoFactorSetupCode('')
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {canDisableTwoFactor && (
        <div className='border rounded p-5 bg-light'>
          <div className='fw-bold mb-2'>Disable two-factor authentication</div>
          <div className='text-muted mb-4'>
            Enter your current password and a valid code from your authenticator app to disable
            2FA.
          </div>

          <Row className='g-4'>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>Current password</Form.Label>
                <Form.Control
                  type='password'
                  value={disableTwoFactorPassword}
                  onChange={(e) => setDisableTwoFactorPassword(e.target.value)}
                  placeholder='Current password'
                />
              </Form.Group>
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>Authenticator code</Form.Label>
                <Form.Control
                  value={disableTwoFactorCode}
                  onChange={(e) =>
                    setDisableTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder='000000'
                  inputMode='numeric'
                />
              </Form.Group>
            </Col>
          </Row>

          <div className='mt-5'>
            <Button
              type='button'
              className='btn btn-light-danger'
              onClick={handleDisableTwoFactor}
              disabled={loadingDisableTwoFactor}
            >
              {!loadingDisableTwoFactor && 'Disable 2FA'}
              {loadingDisableTwoFactor && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </Button>
          </div>
        </div>
      )}

      {canManageOwnTwoFactor && twoFactorEnabled && effectiveTwoFactorRequired && (
        <div className='border rounded p-5 bg-light'>
          <div className='fw-bold mb-2'>Two-factor authentication is required</div>
          <div className='text-muted'>
            Your organization requires 2FA for this account, so it cannot be disabled.
          </div>
        </div>
      )}

      {!canManageOwnTwoFactor && (
        <div className='text-muted fs-7'>
          Each user must connect their own authenticator app from their profile. Administrators can
          reset 2FA and manage email fallback if the user loses access to their phone.
        </div>
      )}
    </div>
  )
}
